/**
 * MutualConnectionsPanel — other-profile right-rail card showing mutual connections.
 *
 * Renders the count headline, an avatar cluster, and a decorative "View all" affordance.
 * Gracefully renders nothing meaningful when user.mutuals is absent or count is 0.
 *
 * Pure component — no hooks.
 */
import styled from 'styled-components';
import { Panel, SectionLabel, Avatar } from '@/ui';
import type { User } from '@/core/models';

/* ── Styled helpers ─────────────────────────────────────────────────────────── */

const CountLine = styled.p`
  margin: 0 0 12px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const AvatarCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 12px;
`;

const ViewAll = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.brandFrom};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Component ──────────────────────────────────────────────────────────────── */

export interface MutualConnectionsPanelProps {
  user: User;
}

export function MutualConnectionsPanel({ user }: MutualConnectionsPanelProps) {
  const mutuals = user.mutuals;
  const hasAny = mutuals && mutuals.count > 0;

  return (
    <Panel>
      <SectionLabel as="h2">Mutual connections</SectionLabel>

      {!hasAny ? (
        <EmptyState>No mutual connections.</EmptyState>
      ) : (
        <>
          <CountLine>
            {mutuals.count} mutual connection{mutuals.count !== 1 ? 's' : ''}
          </CountLine>

          {mutuals.avatars.length > 0 && (
            <AvatarCluster aria-label="Mutual connections avatars">
              {mutuals.avatars.map((a) => (
                <Avatar key={a.name} name={a.name} size={28} />
              ))}
            </AvatarCluster>
          )}

          <ViewAll role="button" tabIndex={0} aria-label="View all mutual connections">
            View all
          </ViewAll>
        </>
      )}
    </Panel>
  );
}
